/**
 * @fileoverview Vuex actions
 * @module store/actions
 * @see module:store/actions
 */
import axios from 'axios';
import router from '@/router';
import { instance } from '@/apis';
import { ElMessage } from 'element-plus';
import { customLink, encrypt, getImageUrl, isOverseasEnv, ryjj_open_postMessage } from '@/utils/api';

import { LOCAL_STORAGE_KEY, DECRYPT_KEY, CLIENT_ID, GET_DEFAULT_USER_INFO, LOGIN_TYPES } from '@/constants';
import { storage, getStorageAuthInfo, cookie, getStorageComeinUid } from '@/utils/storageOperateApi';
import { getCountryAreaCode, getFileUrlByKey, refreshToken, getStartupInfo } from '@/apis/api/common';
import { getMainLineToken } from '@/apis/api/loginUrl';
import { getUserInfo, getUserCustomMenu, getHiddenMenuCodes } from '@/jmHttp/api/user';
import {
  getOrganizationLogo,
  queryUserMembership,
  hotSearchCollect,
  selectOrg,
  loginOut,
  uploadFile,
  ossFileToUpload,
  chunkUpload,
  getCommonConfig
} from '@/jmHttp/api/common';
import { getJmMainLineToken, getAllAuthToken } from '@/apis/api/thirdparty';
import { getSummaryInfo } from '@/jmHttp/api/summary';
import { CLIENT_URL } from '@/jmHttp/host';
import { ROADSHOW_URL_HOST, BRM_URL_HOST, isBuild } from '@/apis/host';
import { logOutUser, overseasLogout } from '@/apis/api/loginOut';
import { getOrCreateDeviceId } from '@/utils/device';
import sensorsTrackJM from '@/common/sensorsTrackJM';
import { resetActivityEntry } from '@/common/hooks/useActivityEntry';
import electron, { isElectron, isElectron_ryjj } from '@/utils/electron';
import { getWorkBuddyHomeQuery, isWorkBuddyClient, OPEN_FROM_WORKBUDDY } from '@/utils/embedEntry';
import i18n from '@/plugins/i18n';
import { emitter } from '@/plugins/emitter';
import slsLogger from '@/utils/slsLogger';
import { beginAuthTransition, endAuthTransition } from '@/utils/authTransition';

const { LOGIN_TYPE, REALM_KEY, SAVE_STORAGE_TYPE } = LOCAL_STORAGE_KEY;
const WORKBUDDY_HISTORY_FROM_BRM = 'brm';

/**
 * 清除新手任务浮窗的本次会话状态。
 * @returns {void}
 */
const clearNewbieTaskVisibleSession = () => {
  const key = LOCAL_STORAGE_KEY.NEWBIE_TASK_VISIBLE_SESSION;
  if (!key) return;
  try {
    sessionStorage.removeItem(key);
  } catch {
    // sessionStorage 不可用时忽略
  }
};

export default {
  /**
   * 提示条
   * @param {*} store
   * @param {*} param1  text : 要展示的文本 duration: 显示时间:默认是 2000
   */
  tooltipsAction(store, { text, duration }) {
    store.commit('showTooltips', text);
    const t = setTimeout(() => {
      store.commit('hideTooltips');
      clearTimeout(t);
    }, duration || 2000);
  },
  /**
   * 分析师主页抽屉
   * @param {*} store
   * @param {*} param1  analystUid : 分析师进门uid
   */
  analystDrawerAction(store, analystInfo) {
    store.commit('showAnalystDrawer', analystInfo);
  },
  /**
   * 获取手机区号
   * @param {*} store
   * @returns
   */
  requestAreaCodeOptions(store) {
    if (store.state.areaCodeOptions && store.state.areaCodeOptions.length !== 0) {
      return store.state.areaCodeOptions;
    }
    return getCountryAreaCode().then((res) => {
      const arr = res.data.map((item) => ({
        label: item.content,
        value: item.keyword,
        id: item.id
      }));
      store.commit('setAreaCodeOptions', arr);
      return arr;
    });
  },

  /**
   * 上传文件
   * @param {*} store
   * @param {*} payload.options 上传参数
   * @param {*} payload.success 上传成功回调
   */
  ossFileUploadNew(store, { options, success }) {
    return new Promise((resolve, reject) => {
      const file = options.file;
      instance
        .post(
          '/api/admin/fileData/uploadFile',
          {
            file: file
          },
          {
            bodyType: 'file'
          }
        )
        .then((res) => {
          success(res.data);
        })
        .catch(reject);
    });
  },

  /**
   * 上传文件
   * @param {*} store
   * @param {*} payload.options 上传参数
   * @param {*} payload.success 上传成功回调
   */
  ossFileUploadNewSetter(store, { options, success, fail, config }) {
    return new Promise((resolve, reject) => {
      const file = options.file;
      const params = {
        file,
        fileType: options.fileType
      };
      instance
        .post('/api/admin/oss/upload', params, {
          bodyType: 'file',
          ...config
        })
        .then((res) => {
          success(res);
          resolve(res);
        })
        .catch((err) => {
          fail(err);
          reject(err);
        });
    });
  },

  /**
   * 音频下载，并获取下载进度
   * @param crz {String} store实例
   * @param obj {Object} 参数对象
   * @param obj.id {String} id
   */
  fetchDownloadProgress(store, obj) {
    let url = obj.url;
    url = url.replace(/\\/g, '/');
    let progressStr = obj.progressStr;
    const name = obj.name;
    const data = obj.data;
    const proArr = store.state.downloadProgress;
    return new Promise((resolve, reject) => {
      axios
        .request({
          url,
          method: 'GET',
          responseType: 'blob',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 30 * 60 * 1000,
          onDownloadProgress: (event) => {
            let b_disable = true;
            if (event.lengthComputable) {
              const str = parseInt((event.loaded * 100) / event.total);
              progressStr = '进度' + str + '%';
              if (typeof obj.progress === 'function') {
                obj.progress(str);
              }
              if (str === 100) {
                progressStr = '下载音频';
                b_disable = false;
              } else {
                // eslint-disable-next-line no-self-assign
                progressStr = progressStr;
                b_disable = true;
              }
            } else {
              progressStr = '下载中';
            }

            const bol = proArr.some((v) => {
              return v.id == data.id;
            });
            if (!bol) {
              proArr.push({ id: data.id, progressStr });
            } else {
              const index = proArr.map((item) => item.id).indexOf(data.id);
              proArr[index].progressStr = progressStr;
              proArr[index].a_disable = b_disable;
            }
            store.commit('setDownloadProgress', proArr);
          }
        })
        .then((res) => {
          if (res.status === 200) {
            const fileName = getFileName(url);
            saveAs(res.data, fileName);

            const index = proArr.map((item) => item.id).indexOf(data.id);
            // proArr[index].progressStr = '下载音频'
            // proArr[index].a_disable = false
            proArr.splice(index, 1);
            resolve(data);
          } else {
            store.dispatch('tooltipsAction', {
              text: '音频下载失败，请复制链接在浏览器打开'
            });
            const index = proArr.map((item) => item.id).indexOf(data.id);
            proArr[index].progressStr = '下载音频';
            proArr[index].a_disable = false;
            reject();
          }
          store.commit('setDownloadProgress', proArr);
          function saveAs(data) {
            const urlObject = window.URL || window.webkitURL || window;
            const export_blob = new Blob([data]);
            const save_link = document.createElementNS('http://www.w3.org/1999/xhtml', 'a');
            save_link.href = urlObject.createObjectURL(export_blob);
            save_link.download = getFileName(url);
            save_link.click();
          }

          function getFileName(url) {
            const num = url.lastIndexOf('.');
            let fileName = url.substring(num);
            //把参数和文件名分割开
            // fileName = decodeURI(fileName.split("?")[0]);
            fileName = name + fileName;
            return fileName;
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  /**
   * 获取用户信息
   * @param {*} store
   * @param {*} payload.data token
   * @param {*} payload.route 路由
   */
  async fetchUserInfoAction() {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      const authUserInfo = getStorageAuthInfo();
      if (!authUserInfo) {
        return reject();
      }
      const goId = authUserInfo?.brmToken?.goId;

      // 判断是否切换了realm
      const currentGoId = storage.get(LOCAL_STORAGE_KEY.REALM_KEY);
      if (![null, undefined, NaN, false, ''].includes(goId) && currentGoId !== goId) {
        storage.set(LOCAL_STORAGE_KEY.REALM_KEY, goId);
      }
      // 判断是否已存在用户信息
      const userInfo = authUserInfo.userinfo;
      // 目前token只提供4个字段，如果有更多字段，需要判断
      if (userInfo && Object.keys(userInfo).length > 4 && userInfo?.empName) {
        return resolve(userInfo);
      }
      try {
        const info = await getUserInfo();
        if (!info || !info.data || String(info.code) !== '0') return ElMessage.error('获取用户信息失败');
        // 同步用户信息到vuex
        this.commit('setUserInfo', info.data);
        authUserInfo.userinfo = info.data;
        storage.set(LOCAL_STORAGE_KEY.BRM_USERINFO, authUserInfo, '', true);
        resolve(info.data);
      } catch (error) {
        reject(error);
      }
    });
  },
  /**
   * 获取用户菜单
   * @param {*} store
   * @param {*} payload.goId goId
   * @param {*} payload.data data
   */
  async fetchUserMenuAction(store, { tokenType = '', token = '', goId = '', payload = '' } = {}) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const headers = {};
        if (tokenType && token) {
          headers['Authorization'] = `${tokenType} ${token}`;
          headers['realm'] = goId;
          headers['goId'] = goId;
          headers['token'] = payload?.token;
          headers['web_token'] = payload?.token;
          // 显式携带本次登录用户的 uid，避免登录/切号时序下被 storage 中残留的旧用户 uid 覆盖，
          // 导致 uid 与 token 不属于同一用户而触发后端「非法身份请求」
          headers['uid'] = payload?.uid;
        }

        const result = await getUserCustomMenu(
          {},
          {
            headers
          }
        );

        if (!result || !result.data || String(result.code) !== '0')
          return ElMessage.error('您当前登录账号没有角色权限，请联系管理员');

        let hiddenMenuCodes = [];
        try {
          const hiddenMenuResult = await getHiddenMenuCodes(
            {},
            {
              headers,
              hideMessage: true
            }
          );
          console.log('hiddenMenuResult11111', hiddenMenuResult);
          hiddenMenuCodes = Array.isArray(hiddenMenuResult?.data)
            ? hiddenMenuResult.data
                .filter((code) => typeof code === 'string')
                .map((code) => code.trim())
                .filter(Boolean)
            : [];
        } catch (error) {
          slsLogger.console({
            component: 'StoreActions',
            message: '用户菜单-查询隐藏菜单失败',
            extra: { error: error?.message || error?.errordesc }
          });
        }

        // 根据接口返回的菜单编码过滤需要隐藏的菜单
        const hiddenMenuCodeSet = new Set(hiddenMenuCodes);
        const allModules = (result.data.allModules || []).filter((menu) => !hiddenMenuCodeSet.has(menu.code));
        const FILTER_MENU_CODE = '/statistics/list';
        const configResult = await getCommonConfig(
          {
            type: 'web_config_statistics_menu'
          },
          {
            headers
          }
        );
        if (configResult && configResult.data && configResult.data.config == 1) {
          allModules.forEach((menu) => {
            if (menu.childModuleList && menu.childModuleList.some((item) => item.code == FILTER_MENU_CODE)) {
              menu.childModuleList = menu.childModuleList.filter((item) => item.code != FILTER_MENU_CODE);
            }
          });
        }

        // 获取用户自定义菜单配置
        const fixedModules = result.data.fixedModules || [];
        const customModules = result.data.customModules || [];
        const moreModules = result.data.moreModules || [];

        // 合并固定菜单和自定义菜单，创建排序索引映射
        const orderedSidebarModules = [...fixedModules, ...customModules];
        const fixedMenuCodes = fixedModules.map((item) => item.code);
        const sortIndexMap = {};
        orderedSidebarModules.forEach((item, index) => {
          sortIndexMap[item.code] = index;
        });

        // 创建更多菜单排序索引，兼容接口返回菜单对象或菜单 code
        const moreSortIndexMap = {};
        // 遍历接口返回的更多菜单，建立 code 到展示顺序的映射
        moreModules.forEach((item, index) => {
          const code = typeof item === 'string' ? item : item.code;
          // 仅记录有效菜单 code，避免无效项污染排序映射
          if (code) moreSortIndexMap[code] = index;
        });

        const data = allModules.map((menu) => ({
          ...menu,
          disableDrag: fixedMenuCodes.includes(menu.code), // 禁止拖拽标识
          isDefaultRoute: menu.code === result.data.defaultModuleCode, // 默认路由标识
          ...(sortIndexMap[menu.code] !== undefined ? { sortIndex: sortIndexMap[menu.code] } : {}), // 侧边菜单栏排序
          ...(moreSortIndexMap[menu.code] !== undefined ? { moreSortIndex: moreSortIndexMap[menu.code] } : {}) // 更多菜单排序
        }));

        return resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  },
  // 由进门登录重定向回来， token uid goid 获取brm token进行登录
  /**
   * 获取brm token
   * @param {*} store
   * @param {*} payload.token token
   * @param {*} payload.uid uid
   * @param {*} payload.go_id go_id
   * @param {*} payload.route 路由
   * @returns
   */
  async fetchBrmTokenAction(store, { token, uid, go_id }) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        storage.set(LOCAL_STORAGE_KEY.REALM_KEY, go_id);
        const result = await getMainLineToken(
          { token, uid, goId: go_id },
          {
            hideLogger: false
          }
        );
        if (!result || !result.data || result.code !== 0) return ElMessage.error('请检查账号密码是否正确');
        const { access_token, refresh_token, token_type, expires_in, refresh_expires_in, roleType } = result.data;
        const brmToken = {
          token: access_token,
          refreshToken: refresh_token,
          tokenType: token_type,
          realm: go_id,
          ...result.data,
          // 备份token过期时间
          b_expires_in: expires_in,
          // token过期时间
          expires_in: expires_in * 1000 + new Date().getTime(),
          refresh_expires_in: refresh_expires_in * 1000 + new Date().getTime(),
          b_refresh_expires_in: refresh_expires_in,
          roleType
        };
        return resolve(brmToken);
      } catch (err) {
        console.error('fetchBrmTokenAction error:', err);
        if (isElectron) {
          electron.loginOut();
        }
        router.push('/login');
        reject(err);
      }
    });
  },

  /**
   * 获取机构logo
   */

  fetchOrgLogoAction(store) {
    return new Promise((resolve, reject) => {
      // 如果是机构设置页面，不请求logo
      if (router.currentRoute.value.name === 'organizationSetting') {
        resolve();
        return;
      }
      getOrganizationLogo()
        .then(async (res) => {
          if (res.code !== '0') {
            store.commit('setOrgLogo', getImageUrl('img/menuLogo.png'));
            reject();
            return;
          }
          store.commit('setOrgInfo', res.data);
          const { logo, exhibitPicUrl, exhibitPicEnUrl } = res.data;
          store.commit('setOrgMiniLogo', logo);
          // 根据中英文取不同的key
          const urlKey = i18n.global.locale.value === 'en-US' ? exhibitPicEnUrl || exhibitPicUrl : exhibitPicUrl;
          if (urlKey) {
            const result = await getFileUrlByKey({ key: urlKey });
            if (result.code !== 0) reject();
            const { url } = result.data;
            store.commit('setOrgLogo', url);
          } else {
            store.commit('setOrgLogo', logo);
          }
          resolve();
        })
        .catch(() => {
          store.commit('setOrgLogo', getImageUrl('img/menuLogo.png'));
          reject();
        });
    });
  },

  /**
   * 刷新token
   */
  refreshTokenAction(store, payload) {
    return new Promise((resolve, reject) => {
      refreshToken(payload, { hideMessage: true })
        .then(async (res) => {
          const { code, data } = res;
          if (code === 0) {
            // 替换本地token
            const authData = storage.get(LOCAL_STORAGE_KEY.BRM_USERINFO, true);
            const { brmToken } = authData;
            const { accessToken, expiresIn, refreshExpiresIn, refreshToken, tokenType } = data;
            authData.brmToken = {
              ...brmToken,
              access_token: accessToken,
              expires_in: expiresIn * 1000 + new Date().getTime(),
              refresh_expires_in: refreshExpiresIn * 1000 + new Date().getTime(),
              refresh_token: refreshToken,
              tokenType,
              // 备份token过期时间
              b_expires_in: expiresIn,
              b_refresh_expires_in: refreshExpiresIn
            };
            await storage.set(LOCAL_STORAGE_KEY.BRM_USERINFO, authData, '', true);
            resolve(res);
          } else {
            reject();
          }
        })
        .catch(() => {
          reject({ msg: '用户信息已过期，请重新登陆' });
        });
    });
  },

  /**
   * 获取进门用户信息（选择机构后）
   */
  fetchJmUserInfoAction(store, { outsideOrgId, webtoken }) {
    return new Promise((resolve, reject) => {
      selectOrg(
        {
          outsideOrgId
        },
        {
          bodyType: 'form',
          headers: {
            webtoken
          }
        }
      )
        .then(async ({ code, data, errordesc }) => {
          if (code !== '0') {
            ElMessage.error(errordesc || '获取用户信息失败');
            return resolve({});
          }
          resolve(data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  /**
   * 获取进门token信息
   */
  fetchJmTokenAction(store, payload) {
    return new Promise((resolve, reject) => {
      getJmMainLineToken(payload)
        .then(async (res) => {
          if (res.code !== 0) {
            ElMessage.error('获取进门token失败');
            return reject();
          }
          resolve(res.data);
        })
        .catch(() => {
          reject();
        });
    });
  },

  /**
   * 检验纪要跳转逻辑
   * 判断是否有已发布的会议全文
   * @param {*} store
   * @param {*} payload.roadshowId 路演id
   * @returns {Promise}
   */
  checkSummaryDetailJumpAction(
    store,
    { id, needPush = false, sessionId, tip, openBrmPageInNewTab = false, useReplace = false, hash = '' }
  ) {
    return new Promise((resolve, reject) => {
      getSummaryInfo({ roadshowId: id })
        .then(({ code, data }) => {
          if (Number(code) !== 0) return reject();
          const { aiSummary, meetSummary } = data;
          const { aiSummaryId, chatEnable, usePoints } = aiSummary || {};
          // releaseStatus 0 未发布 1 已发布
          // status 0 进行中 1 转写完成 2 失败
          if (!meetSummary || !meetSummary.summaryId) {
            ElMessage.error(tip || i18n.global.t('common.tip.meeting_no_summary'));
            return resolve();
          }
          // 存在已发布的会议全文 跳转纪要页面
          const { summaryId, tranEnable, tranStatus, tranType } = meetSummary;
          // 路演第三方平台：仍跳转 BRM 纪要详情页，但在新浏览器标签打开（兼容 iframe 嵌入场景）
          if (openBrmPageInNewTab) {
            const brmPage = router.resolve({
              name: 'roadshowMeeting',
              query: {
                aiSummaryId,
                chatEnable,
                summaryId,
                id,
                openTranslate: tranEnable === 1 && tranStatus === 2,
                tranType,
                usePoints,
                sessionId
              }
            });
            window.open(brmPage.href, '_blank');
            resolve();
            return;
          }
          const isThirdParty = Boolean(store.state.bridgeInfo?.isThirdParty);
          const routeTarget = {
            name: isThirdParty ? 'instituteSummaryDetail' : 'roadshowMeeting',
            query: {
              aiSummaryId,
              chatEnable,
              summaryId,
              id,
              openTranslate: tranEnable === 1 && tranStatus === 2,
              tranType,
              usePoints,
              sessionId,
              miniTranType: tranType,
              ...(isThirdParty ? { brmChannel: store.state.bridgeInfo?.thirdparty } : {})
            },
            ...(hash ? { hash } : {})
          };

          if (useReplace) {
            router.replace(routeTarget).catch(() => {});
            resolve();
            return;
          }

          if (isThirdParty) {
            store.state.bridgeInfo?.goSummaryDetail(routeTarget);
            resolve();
            return;
          }

          const page = router.resolve(routeTarget);
          customLink(page, needPush);
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  },

  /**
   * 进入路演直播厅（直播/回放）
   */
  enterRoadshowLiveAction(store, payload) {
    logger.log('进入路演直播厅', payload);
    store.dispatch('jm/checkTokenAction').then(async (checkTokenResult) => {
      const { meetId, meetingId, contentId, id, roadshowId, redirect_uri, callback, meetType, queryParams } = payload;
      const isWorkBuddyEntry = isWorkBuddyClient();
      const jmUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
      const webtoken =
        checkTokenResult?.data?.webtoken ||
        checkTokenResult?.data?.webToken ||
        checkTokenResult?.data?.web_token ||
        jmUserInfo.webtoken ||
        jmUserInfo.webToken ||
        jmUserInfo.web_token ||
        jmUserInfo.token;
      const uid = checkTokenResult?.data?.uid || jmUserInfo.uid || jmUserInfo.userinfo?.uid;
      if (!webtoken || uid === null || uid === undefined || uid === '') {
        slsLogger.console({
          component: 'StoreActions',
          level: 'error',
          message: '路演跳转-登录信息缺失',
          extra: { hasToken: Boolean(webtoken), hasUid: uid !== null && uid !== undefined && uid !== '' }
        });
        return ElMessage.error('获取用户信息失败');
      }
      isElectron && logger.log('jmUserInfo', jmUserInfo);
      const userParams = {
        token: webtoken,
        uid
      };
      if (isOverseasEnv()) {
        userParams.overseasToken = cookie.get(LOCAL_STORAGE_KEY.OVERSEAS_TOKEN) || '';
        userParams.lang = i18n.global.locale.value;
      }

      const userInfo = encodeURIComponent(encrypt(JSON.stringify(userParams), DECRYPT_KEY));

      // 路演直播厅地址
      let pathname = '/roadshow/home';
      if (meetType === 2) {
        pathname = '/roadshow/meeting';
      }

      if (!store.state.orgLogo) {
        await store.dispatch('fetchOrgLogoAction');
      }

      let path = `${redirect_uri ? redirect_uri : ROADSHOW_URL_HOST}${pathname}/${
        contentId || meetId || meetingId || id || roadshowId
      }?&userInfo=${userInfo}`;
      if (meetType === 2 && isElectron) {
        path += '&from=brm';
      }

      const workBuddyHomeQuery = isWorkBuddyEntry ? getWorkBuddyHomeQuery() : {};
      const portalQueryParams = {
        ...queryParams,
        ...(isWorkBuddyEntry ? { openFrom: OPEN_FROM_WORKBUDDY, ...workBuddyHomeQuery } : {}),
        // 当前仅处理 WorkBuddy 路演页；后续其他 Portal 页面需要历史导航时再扩展 pathname 范围。
        ...(isWorkBuddyEntry && pathname === '/roadshow/home' ? { historyFrom: WORKBUDDY_HISTORY_FROM_BRM } : {})
      };
      Object.entries(portalQueryParams).forEach(([key, value]) => {
        path += `&${key}=${encodeURIComponent(value)}`;
      });

      if (isWorkBuddyEntry) {
        slsLogger.console({
          component: 'StoreActions',
          message: '路演跳转-透传WorkBuddy来源',
          extra: {
            openFrom: OPEN_FROM_WORKBUDDY,
            hasWorkBuddyHome: Object.keys(workBuddyHomeQuery).length > 0,
            ...(pathname === '/roadshow/home' ? { historyFrom: WORKBUDDY_HISTORY_FROM_BRM } : {})
          }
        });
      }

      let options = {
        comein_channel: 'brm'
      };
      if (store.state.bridgeInfo?.isThirdParty || storage.get(LOCAL_STORAGE_KEY.COOPERATION, true)) {
        const thirdPartyInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
        options = {
          brmChannel: thirdPartyInfo.userChannel || thirdPartyInfo.comein_channel || thirdPartyInfo.brmChannel,
          errPath: `${BRM_URL_HOST}/thirdparty/instituteNotFound`,
          loginMethod: 1,
          ...options
        };
      }
      if (isElectron) {
        options.hideHome = true;
        options.isElectron = true;
      }
      isElectron && logger.log('options', options);
      path += `&options=${encodeURIComponent(encrypt(JSON.stringify(options), DECRYPT_KEY))}`;

      if (isElectron) {
        path = router.resolve({
          name: 'commonRoadshowDetail',
          query: {
            url: encodeURIComponent(path)
          }
        });
      }
      //睿远基金单独调方法
      if (isElectron_ryjj) {
        return ryjj_open_postMessage(path);
      }

      if (callback) return callback(path);
      // electron 标签
      let title = '路演详情';
      if (payload.title && payload.status) {
        if (payload.status === '3') {
          title = `[回放]${payload.title}`;
        } else {
          title = `[路演]${payload.title}`;
        }
      }
      if (meetType === 2) {
        title = `[自助会议]${payload.title || payload.subject}`;
      }
      isElectron && logger.log('进入路演直播厅path', path, title);
      customLink(path, undefined, title);
    });
  },

  /**
   * 进入进门页面
   * @param {*} store
   * @param {*} payload
   * @returns
   */
  enterComeinPage(store, payload) {
    const { redirect_uri, callback } = payload;
    const jmUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO);
    const { organizationId, webtoken, uid } = jmUserInfo;
    const userInfo = encodeURIComponent(encrypt(JSON.stringify({ token: webtoken, uid }), DECRYPT_KEY));
    const isWorkBuddyEntry = isWorkBuddyClient();

    // 额外参数
    const params = {
      comein_channel: 'brm',
      orgId: organizationId,
      logo: store.state.orgLogo
    };
    const options = encodeURIComponent(encrypt(JSON.stringify(params), DECRYPT_KEY));

    const path = `${redirect_uri}?&comein_channel=brm&userInfo=${userInfo}&options=${options}${
      isWorkBuddyEntry ? `&openFrom=${OPEN_FROM_WORKBUDDY}` : ''
    }`;

    if (isWorkBuddyEntry) {
      slsLogger.console({
        component: 'StoreActions',
        message: 'Portal跳转-透传WorkBuddy来源',
        extra: { openFrom: OPEN_FROM_WORKBUDDY }
      });
    }

    if (callback) return callback(path);
  },

  /**
   * 退出登录
   * @param {*} store
   * @param {*} payload.clearStorage 是否清除本地缓存
   * @returns {Promise}
   */
  logoutAction(store, clearStorage = true) {
    // 退出后清除模块级缓存，确保下次登录按新账号重新查询活动入口
    resetActivityEntry();
    emitter.emit('progress-center-clear-all');
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      if (isElectron) {
        clearNewbieTaskVisibleSession();
        location.href = `${CLIENT_URL}/login`;
        return resolve();
      }
      const storageAuthInfo = getStorageAuthInfo();
      const brmToken = storageAuthInfo?.brmToken;
      if (!brmToken) {
        clearNewbieTaskVisibleSession();
        if (clearStorage) {
          storage.clear();
          store.commit('clearState');
          store.commit('jm/clearState');
          // 同步清理 AI 聊天模块的所有用户级缓存（标签 / 投研大脑 / 通知 等），
          // 避免无 brmToken 但已有残留缓存的场景下，新用户读到上个账号的数据
          try {
            store.commit('comeinAiChat/clearState');
          } catch (e) {
            console.log(e);
          }
          cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });
          delete window.comeinChannel;
          try {
            sessionStorage.removeItem('THIRD_PARTY_SESSION');
          } catch {
            // sessionStorage 不可用时忽略
          }
        }
        return resolve();
      }
      // 主线退出
      const logoutApi = isOverseasEnv() ? overseasLogout : loginOut;
      const result = await logoutApi();
      if (result.code != 0) return reject();
      const { refresh_token, goId } = brmToken || {};
      const logoutFn = () => {
        clearNewbieTaskVisibleSession();
        // 清除缓存
        if (clearStorage) {
          // 清理 AI 聊天实例，避免路由卸载时触发保存设置请求
          try {
            store.commit('comeinAiChat/clearComeinChat');
          } catch (e) {
            console.log(e);
          }
          // 重置整个 comeinAiChat 模块到初始 state：清掉标签列表 / 投研大脑列表 /
          // 通知队列 / loaded 标记等所有用户级缓存，避免切换账号后新用户读到上个用户的数据
          try {
            store.commit('comeinAiChat/clearState');
          } catch (e) {
            console.log(e);
          }
          const keepKeys = ['lang'];
          const keepValues = {};

          // 1. 先保存要保留的 key 的值
          keepKeys.forEach((key) => {
            keepValues[key] = localStorage.getItem(key);
          });
          storage.clear();
          store.commit('clearState');
          store.commit('jm/clearState');
          cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });
          if (isOverseasEnv()) {
            cookie.remove(LOCAL_STORAGE_KEY.OVERSEAS_TOKEN, { domain: '.finenter.ai' });
            cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.finenter.ai' });
          }

          // 清理第三方登录残留的全局状态
          delete window.comeinChannel;
          try {
            sessionStorage.removeItem('THIRD_PARTY_SESSION');
          } catch {
            // sessionStorage 不可用时忽略
          }

          // 3. 恢复要保留的 key
          Object.entries(keepValues).forEach(([key, value]) => {
            if (value !== null) {
              if (key == 'lang') {
                store.commit('setLang', value);
              } else {
                localStorage.setItem(key, value);
              }
            }
          });
        }
      };
      // brm退出
      logOutUser(goId, {
        clientId: CLIENT_ID,
        refreshToken: refresh_token
      })
        .then((res) => {
          if (res.code !== 0) return reject();
          logoutFn();
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  },

  /**
   * 根据url上的密文获取brm token 和 jm token
   * @param {*} store
   * @param {*} payload
   * @returns
   */
  getAllTokenByUrlData(store, payload) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const { userInfo, userChannel, fetchMenus } = payload;

        // 清空旧用户信息，避免请求携带旧的uid  导致请求的旧uid和新token对应不上
        storage.remove(LOCAL_STORAGE_KEY.BRM_USERINFO);
        storage.remove(LOCAL_STORAGE_KEY.JM_AUTH_INFO);

        const result = await getAllAuthToken({ userInfo: userInfo, userChannel }, { needTime: true });
        if (result?.code !== 0) return reject(result);
        const { data } = result;
        const {
          goId,
          userId,
          phoneNumber,
          comeinUid,
          initialPassword,
          roleId,
          areacode,
          tokenComeBrm,
          tokenComeIn,
          tokenType,
          refreshToken,
          expiresIn,
          refreshExpiresIn,
          avatarurl,
          login
        } = data;

        // 生产鉴权信息模版
        const authInfo = GET_DEFAULT_USER_INFO();
        storage.set(REALM_KEY, goId);
        storage.set(LOGIN_TYPE, LOGIN_TYPES.PARTNER);
        store.commit('setGoId', goId);
        storage.set(SAVE_STORAGE_TYPE, 1);
        authInfo.userinfo = {
          goId,
          id: userId,
          roleId,
          phoneNumber,
          comeinUid,
          initialPassword,
          countryCode: areacode
        };
        authInfo.brmToken = {
          token: tokenComeBrm,
          access_token: tokenComeBrm,
          refreshToken: refreshToken,
          refresh_token: refreshToken,
          tokenType,
          realm: goId,
          userId,
          goId,
          comeinUid,
          initialPassword,
          b_expires_in: expiresIn,
          b_refresh_expires_in: refreshExpiresIn,
          expires_in: expiresIn * 1000 + new Date().getTime(),
          refresh_expires_in: refreshExpiresIn * 1000 + new Date().getTime()
        };
        authInfo.menus = [{}];
        payload.token = tokenComeIn;
        // 是否需要请求用户菜单
        if (fetchMenus) {
          const userMenu = await store.dispatch('fetchUserMenuAction', {
            tokenType,
            token: tokenComeBrm,
            goId,
            payload: payload
          });
          authInfo.menus = userMenu;
        }
        storage.set(LOCAL_STORAGE_KEY.BRM_USERINFO, authInfo, '', true);
        // 同步到vuex
        await store.commit('initState', { authInfo, goId: authInfo?.brmToken?.goId });

        const jmAuthInfo = {
          uc: userChannel,
          avatarurl,
          uid: comeinUid,
          phonenumber: phoneNumber,
          organizationId: goId,
          outsideOrgId: goId,
          webtoken: tokenComeIn,
          userinfo: {
            areacode,
            avatarurl,
            login,
            phonenumber: phoneNumber,
            token: tokenComeIn,
            webtoken: tokenComeIn,
            uuid: comeinUid,
            uid: comeinUid
          }
        };
        avatarurl && store.commit('setUserAvatar', avatarurl);
        storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, jmAuthInfo, '', true);

        return resolve({
          data,
          authInfo,
          jmAuthInfo
        });
      } catch (error) {
        return reject(error);
      }
    });
  },

  /**
   * 获取BRM token and menu
   */
  fetchUserTokenAndMenuAction(store, payload) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const { goId, token, uid } = payload;
        // 下面会先清空本地鉴权、再经两次网络请求才写回新鉴权。localStorage 多标签页共享，
        // 先落换发标记，避免同浏览器其它已打开的页面把这段空窗期误判成登录失效
        beginAuthTransition('fetchUserTokenAndMenuAction');
        // 本地缓存登录信息
        store.commit('clearState');
        store.commit('jm/clearState');
        // 登录新账号前清掉 AI 聊天模块的所有用户级缓存，避免上一个账号的标签 /
        // 投研大脑列表 / 通知等残留被新账号页面误读
        try {
          store.commit('comeinAiChat/clearState');
        } catch (e) {
          console.log(e);
        }

        // 合作页已登录下再次登录不清除合作标识
        // 必须走 storage.clear()：原生 localStorage.clear() 不认 isClear 标记，会把
        // 记住密码、语言、设备标识等声明为「跨登录保留」的偏好一并清掉
        if (!storage.get(LOCAL_STORAGE_KEY.COOPERATION, true)) {
          storage.clear();
        }

        storage.set(LOGIN_TYPE, LOGIN_TYPES.JM);
        storage.set(REALM_KEY, goId);
        // 目前默认为1
        storage.set(LOCAL_STORAGE_KEY.REALM_KEY, 1);
        // 生产鉴权信息模版
        const authInfo = GET_DEFAULT_USER_INFO();
        // 获取BRM token
        const brmToken = await store.dispatch('fetchBrmTokenAction', { token, uid, go_id: goId });
        authInfo.brmToken = brmToken;
        // 获取用户菜单
        const userMenu = await store.dispatch('fetchUserMenuAction', {
          tokenType: brmToken?.tokenType,
          token: brmToken?.token,
          goId,
          payload: payload
        });
        authInfo.menus = userMenu;
        // 本地缓存用户信息
        storage.set(LOCAL_STORAGE_KEY.BRM_USERINFO, authInfo, '', true);
        // 同步vuex
        await store.commit('initState', { authInfo, goId });
        resolve(authInfo);
      } catch (e) {
        logger.log('获取brm token and menu 失败', e);
        reject(e);
      } finally {
        endAuthTransition();
      }
    });
  },

  /**
   * 获取用户会员信息
   */
  fetchUserMembershipAction(store) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const res = await queryUserMembership();
        if (+res.code === 0 && res.data) {
          store.commit('setUserMembership', res.data);
        }
        resolve(res);
      } catch (e) {
        logger.log('获取获取用户会员信息失败', e);
        reject(e);
      }
    });
  },

  // 记录搜索id，添加搜索记录
  handleHotSearchCollect(store, params) {
    return new Promise((resolve) => {
      const comeinUid = getStorageComeinUid();
      const { keyword, firstPage, secondPage } = params;
      const keywordVal = keyword?.trim();
      const searchId = keywordVal ? `${Date.now()}_${comeinUid}` : '';
      const sourcePageKey = `${firstPage}${secondPage ? `_${secondPage}` : ''}`;
      store.commit('setGlobalSearchIdMap', { searchId, sourcePageKey });
      keywordVal &&
        hotSearchCollect({
          userChannel: 'WEB',
          physicalAddress: getOrCreateDeviceId(), // 设备标识，用uuid代替
          searchContent: keywordVal,
          searchId,
          firstPage,
          secondPage
        });
      resolve();
    });
  },

  /**
   * 搜索结果点击埋点，上报点击位次
   * @param {*} store
   * @param {*} params
   * @returns
   */
  searchResultClickTrack(store, params) {
    const { firstPage, secondPage, listIndex, ...rest } = params;
    const pageKey = `${firstPage}${secondPage ? `_${secondPage}` : ''}`;
    const searchId = store.getters.getGlobalSearchId(pageKey);
    // 如果没有搜索id，或没有搜索关键字，就不执行上报
    if (!searchId || !params.input) return;

    const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true);
    const jmUserLoginInfo = jmAuthInfo?.userinfo?.userLoginIdentity || {};
    const { name, orgName, shortOrgName } = jmUserLoginInfo;
    const trackParams = {
      searchId,
      ...rest,
      userName: name,
      orgName,
      shortOrgName,
      firstPage,
      secondPage,
      listIndex
    };
    sensorsTrackJM({
      // 搜索结果-详情
      statisticnum: 'SEARCH.child.SEARCH_RESULT.child.DETAIL',
      extra: JSON.stringify(trackParams)
    });
  },

  /**
   * 调起会议录制客户端
   * @param {*} store
   * @param {*} url 跳转地址 默认是login(登录) join-meeting(进入会议)
   * @returns
   */
  launchMeetingRecordClient(store, { url = 'login' }) {
    return new Promise((resolve, reject) => {
      const jmUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO);
      const brmUserInfo = getStorageAuthInfo();

      const userInfo = encodeURIComponent(
        JSON.stringify({
          token: jmUserInfo.webtoken,
          uid: jmUserInfo.uid,
          // 机构id
          goId: jmUserInfo.outsideOrgId,
          // 用户头像
          avatar: jmUserInfo.avatarurl,
          brmToke: `${brmUserInfo?.brmToken?.tokenType} ${brmUserInfo?.brmToken?.access_token}`,
          comeinUid: brmUserInfo?.userinfo?.comeinUid
        })
      );
      try {
        const protocol = `${isBuild ? 'brmmeeting' : 'dev-brmmeeting'}`;
        const urlToOpen = `${protocol}://${url}?userInfo=${userInfo}`;
        window.urlToOpen = urlToOpen;
        // 尝试打开 URL
        window.location.href = urlToOpen;
        const MAX_COUNT = 6;
        // 2s后检测是否打开了客户端
        setTimeout(() => {
          // 判断本地是否启动了客户端
          let count = 0;
          const timer = setInterval(() => {
            count++;
            getStartupInfo({ type: 'brm-meeting-client' })
              .then((res) => {
                if (res.code !== 0 || !res.data) return;
                if (res.code === 0) {
                  const is = JSON.parse(res.data)?.comeinUid == brmUserInfo.userinfo.comeinUid;
                  if (!is) return;
                  ElMessage('客户端已启动');
                  resolve({ code: 0, message: '客户端已启动' });
                  clearInterval(timer);

                  // 浏览器上报接收信息成功 暂时不需要
                  // reportStartupInfo({
                  //   type: 'brm-meeting-manage',
                  //   value: JSON.stringify({ comeinUid: brmUserInfo.userinfo.comeinUid })
                  // });
                }
              })
              .catch((error) => {
                clearInterval(timer);
                reject({ code: 1, message: '客户端未启动', error });
              })
              .finally(() => {
                // 6次轮询后未启动客户端 3秒
                if (count >= MAX_COUNT) {
                  clearInterval(timer);
                  reject({ code: 1, message: '客户端未启动' });
                }
              });
          }, 500);
        }, 2000);
      } catch (error) {
        reject({ code: 1, message: '客户端未启动', error });
      }
    });
  },

  /**
   * 上传文件到阿里云
   * param {Object} options: {fileType: 场景类型, file: 文件对象}
   * param {Function} onProgress: 上传进度回调函数，接收参数为进度百分比 (0-100)
   */
  uploadFileToAliyun(store, { options, onProgress }) {
    return new Promise((resolve, reject) => {
      const signal = options.signal;
      const uploadConfig = signal ? { signal } : {};
      // 根据不同场景传fileType
      uploadFile(
        {
          fileType: options.fileType
        },
        uploadConfig
      )
        .then(({ data }) => {
          const { host, accessid, callback, dir, policy, signature, bucketName, requestId, fileType } = data;
          const blob = new Blob([options.file]);
          const body = {
            key: dir + '.' + options.file.name.split('.').pop(),
            policy,
            OSSAccessKeyId: accessid,
            callback,
            signature,
            file: blob,
            bucketName,
            requestId,
            fileType
          };

          // 配置上传进度回调
          const config = {
            bodyType: 'file'
          };

          // 如果提供了 onProgress 回调，添加到配置中
          if (typeof onProgress === 'function') {
            config.onUploadProgress = (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onProgress(percentCompleted);
              }
            };
          }

          if (signal) {
            config.signal = signal;
          }

          ossFileToUpload(host, body, config)
            .then((res) => {
              res.body = body;
              resolve(res);
            })
            .catch((error) => {
              reject(error);
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * 中断分片上传
   * @param {*} store
   * @param {String} fileId - 文件唯一标识（必填）
   */
  async clearAbortCheckpoint(store, fileId) {
    if (!fileId) {
      return Promise.reject(new Error('缺少 fileId 参数'));
    }
    const uploadTask = store.state.uploadTasks[fileId];

    if (!uploadTask) {
      return;
    }
    const { ossClient, checkpoint } = uploadTask;
    const { name, uploadId } = checkpoint;

    if (!ossClient || !checkpoint || !name || !uploadId) {
      store.commit('removeUploadTask', fileId);
      return;
    }
    try {
      await ossClient.abortMultipartUpload(name, uploadId);
      // 删除该上传任务
      store.commit('removeUploadTask', fileId);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * 中断所有分片上传
   * @param {*} store
   */
  async clearAllUploadTasks(store) {
    const uploadTasks = store.state.uploadTasks;
    const fileIds = Object.keys(uploadTasks);
    if (fileIds.length === 0) {
      return;
    }
    // 只中断进行中的上传任务（有 checkpoint 且有 uploadId 的）
    const inProgressFileIds = fileIds.filter((fileId) => {
      const task = uploadTasks[fileId];
      return task.checkpoint && task.checkpoint.uploadId;
    });
    // 中断进行中的任务
    inProgressFileIds.forEach((fileId) => store.dispatch('clearAbortCheckpoint', fileId));
    store.commit('removeAllUploadTasks');
  },
  /**
   * 分片上传文件到阿里云 OSS
   * @param {Object} options - 上传选项
   * @param {File} options.file - 要上传的文件对象
   * @param {String} options.fileType - 场景类型
   * @param {String} options.fileId - 文件唯一标识（可选，不提供则自动生成）
   * @param {Number} options.partSize - 分片大小（字节），默认 1MB，建议 1MB-5MB
   * @param {Number} options.parallel - 并发上传数，默认 3
   * @param {Function} onProgress - 上传进度回调函数，接收参数为进度百分比 (0-100) 和 fileId
   * @returns {Promise}
   */
  async chunkUploadFileToAliyun(store, { options, onProgress }) {
    // 生成或使用传入的 fileId
    const fileId = options.fileId || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fileName = options.file.name;

    try {
      const credentialsRes = await chunkUpload({
        fileType: options.fileType
      });

      if (!credentialsRes || credentialsRes.code !== '0') return;

      const { data } = credentialsRes;
      const { bucket, AccessKeyId, AccessKeySecret, SecurityToken, ObjectPre, callback } = data;
      if (!bucket || !AccessKeyId || !AccessKeySecret || !SecurityToken) return;

      const REGION = 'oss-cn-hangzhou';
      const fileExtension = fileName.split('.').pop();
      const objectKey = `${ObjectPre}.${fileExtension}`;

      // 初始化 OSS 客户端
      const OSS = (await import('ali-oss')).default;
      const client = new OSS({
        REGION,
        authorizationV4: true,
        accessKeyId: AccessKeyId,
        accessKeySecret: AccessKeySecret,
        stsToken: SecurityToken,
        bucket: bucket,
        timeout: 600000, // 10分钟
        refreshSTSToken: async () => {
          try {
            const refreshRes = await chunkUpload({ fileType: options.fileType });
            if (refreshRes && refreshRes.code === '0' && refreshRes.data) {
              return {
                accessKeyId: refreshRes.data.AccessKeyId,
                accessKeySecret: refreshRes.data.AccessKeySecret,
                stsToken: refreshRes.data.SecurityToken
              };
            }
          } catch (error) {
            console.error('刷新 STS token 失败:', error);
            throw error;
          }
        },
        // STS token 刷新间隔，单位毫秒，默认1小时
        refreshSTSTokenInterval: 3600000
      });

      // 配置分片上传参数
      const partSize = options.partSize || 1 * 1024 * 1024; // 默认 1MB
      const parallel = options.parallel || 3;
      const blob = new Blob([options.file]);

      const result = await client.multipartUpload(objectKey, blob, {
        partSize: partSize,
        parallel: parallel,
        progress: (p, checkpoint) => {
          const percentCompleted = Math.round(p * 100);
          store.commit('setUploadTask', {
            fileId,
            checkpoint,
            ossClient: client
          });
          if (typeof onProgress === 'function') {
            onProgress(percentCompleted, checkpoint);
          }
        },
        callback: {
          body: callback.callbackBody,
          url: callback.callbackUrl,
          contentType: callback.callbackBodyType
        }
      });
      const { viewurl, filename } = result.data;

      // 上传成功后移除任务，避免后续清理时出现 404
      store.commit('removeUploadTask', fileId);

      return {
        code: '0',
        fileId, // 返回 fileId
        viewurl,
        bucketName: result.bucket,
        bucketKey: objectKey,
        filename,
        msg: '上传成功'
      };
    } catch (error) {
      // 上传失败后移除任务
      store.commit('removeUploadTask', fileId);
      return Promise.reject({
        fileId, // 返回 fileId
        code: error.code || '-1',
        msg: error.message || '分片上传失败',
        error: error,
        details: {
          requestId: error.requestId,
          hostId: error.hostId,
          status: error.status
        }
      });
    }
  },
  /**
   * //用户异常验证 手机号/邮箱
   * param {obj} options: {
   * securityAuth: 是否显示认证,
   * securityAuthType: UUID,
   * securityAuthContent:认证手机号或邮箱,
   * securityAuthCloseBtn: 认证取消,
   * securityAuthSuccessBtn: 认证成功
   * }
   */
  showSecurityAuthDialog(store, obj) {
    store.commit('setSecurityAuth', obj);
  },
  hideSecurityAuthDialog(store) {
    store.commit('setSecurityAuth', {
      securityAuth: false
    });
  },

  showSecurityFaceDialog(store, obj) {
    store.commit('setSecurityFace', obj);
  },

  hideSecurityFaceDialog(store) {
    store.commit('setSecurityFace', {
      security_face: false
    });
  },

  /**
   * 校验本地是否存在合作页token并且判断是否过期
   */
  checkThirdPartyTokenAction(store, routerQuery) {
    return new Promise((resolve) => {
      const localThirdPartyInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true) || {};
      const { userChannel, userInfo } = localThirdPartyInfo;
      const routerUserChannel = routerQuery?.userChannel || routerQuery?.comein_channel;
      // 用户信息不存在或者用户信息不一致
      if (!localThirdPartyInfo || userChannel !== routerUserChannel || userInfo !== routerQuery?.userInfo)
        return resolve(true);
      // token过期
      const localBrmToken = storage.get(LOCAL_STORAGE_KEY.BRM_USERINFO, true)?.brmToken;
      const localJmToken = storage.get(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true);
      if (!localBrmToken || !localJmToken || localBrmToken.expires_in < new Date().getTime()) return resolve(true);
      return resolve(false);
    });
  }
};
